import React, { useState } from 'react';
import axios from 'axios';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { errText } from '../../account/ChangePasswordCard';

const API = process.env.REACT_APP_BACKEND_URL;

// Super Admin: permanently delete an Active/Inactive user. Requires typing their email.
export const DeleteUserDialog = ({ user, token, onClose, onDeleted }) => {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const match = typed.trim().toLowerCase() === (user.email || '').toLowerCase();

  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/api/admin/company-users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }, params: { confirm_email: typed.trim() },
      });
      toast.success(data.message);
      onDeleted();
    } catch (e) {
      toast.error(errText(e, 'Could not delete user'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="delete-user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700"><AlertTriangle className="h-5 w-5" /> Permanently delete user</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-3 text-sm text-neutral-600">
              <p className="rounded-md bg-neutral-50 p-3"><strong className="text-neutral-900" data-testid="delete-user-name">{user.name || '—'}</strong><br /><span data-testid="delete-user-email">{user.email}</span></p>
              <p>This <strong>cannot be undone</strong>. They are signed out everywhere and their login, password, authenticator, backup codes and personal settings are removed. Invoices, orders, payments and logs are kept and marked as a deleted user.</p>
              <p>To keep the option of restoring access, use <strong>Deactivate</strong> instead.</p>
            </div>
          </DialogDescription>
        </DialogHeader>
        <div>
          <label className="text-sm font-medium" htmlFor="delete-confirm">Type <span className="font-mono">{user.email}</span> to confirm</label>
          <Input id="delete-confirm" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" className="mt-2" data-testid="delete-user-confirm-input" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="delete-user-cancel">Cancel</Button>
          <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={!match || busy} onClick={remove} data-testid="delete-user-confirm-btn">
            {busy ? 'Deleting…' : 'Delete Permanently'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
